import { useEffect, useRef, useState } from "react";

import { useBlockNumber } from "./useBlockNumber";
import { useRpcRequest } from "./useRpcRequest";

/**
 * Lightweight hook that tracks cumulative TX and event counts per block.
 * Used to drive sidebar notification badges — adds ~2 RPC calls per block.
 *
 * @param fromBlock - Optional starting block (e.g. deployment block) to count
 *   from on first mount. When provided after the hook has already started
 *   counting, the hook back-fills the missed range.
 */
export function useActivityCounts(fromBlock?: number) {
  const { data: blockNumber } = useBlockNumber();
  const { request } = useRpcRequest();
  const [txCount, setTxCount] = useState(0);
  const [eventCount, setEventCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [syncRange, setSyncRange] = useState<{
    from: number;
    to: number;
  } | null>(null);
  const lastBlock = useRef(0);
  const appliedFrom = useRef<number | undefined>(undefined);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!blockNumber) return;
    const bn = Number(blockNumber);

    // If fromBlock just became available and we already started counting from
    // a later block, back-fill the gap.
    let backfillStart: number | undefined;
    if (
      fromBlock !== undefined &&
      appliedFrom.current === undefined &&
      lastBlock.current > 0 &&
      fromBlock < lastBlock.current
    ) {
      backfillStart = Math.max(1, fromBlock);
      appliedFrom.current = fromBlock;
    }

    if (fromBlock !== undefined && appliedFrom.current === undefined) {
      appliedFrom.current = fromBlock;
    }

    if (fetchingRef.current) return;
    if (bn <= lastBlock.current && backfillStart === undefined) return;

    const startBlock =
      lastBlock.current === 0
        ? Math.max(1, fromBlock ?? bn - 5)
        : lastBlock.current + 1;
    const syncStart =
      backfillStart !== undefined
        ? Math.min(backfillStart, startBlock)
        : startBlock;

    setIsSyncing(true);
    setSyncRange({ from: syncStart, to: bn });
    fetchingRef.current = true;

    async function fetchCounts() {
      let newTxs = 0;
      let newEvents = 0;

      try {
        // Back-fill missed blocks if deployment info arrived late
        if (backfillStart !== undefined) {
          const backfillEnd = startBlock - 1;
          for (let b = backfillStart; b <= backfillEnd; b++) {
            try {
              const block = (await request("eth_getBlockByNumber", [
                "0x" + b.toString(16),
                false,
              ])) as { transactions?: string[] } | null;
              if (block?.transactions) {
                newTxs += block.transactions.length;
              }
            } catch {
              // skip
            }
          }

          try {
            const logs = await request("eth_getLogs", [
              {
                fromBlock: "0x" + backfillStart.toString(16),
                toBlock: "0x" + backfillEnd.toString(16),
              },
            ]);
            if (Array.isArray(logs)) {
              newEvents += logs.length;
            }
          } catch {
            // skip
          }
        }

        // Fetch new blocks
        for (let b = startBlock; b <= bn; b++) {
          try {
            const block = (await request("eth_getBlockByNumber", [
              "0x" + b.toString(16),
              false,
            ])) as { transactions?: string[] } | null;
            if (block?.transactions) {
              newTxs += block.transactions.length;
            }
          } catch {
            // skip
          }
        }

        try {
          const logs = await request("eth_getLogs", [
            {
              fromBlock: "0x" + startBlock.toString(16),
              toBlock: "0x" + bn.toString(16),
            },
          ]);
          if (Array.isArray(logs)) {
            newEvents += logs.length;
          }
        } catch {
          // skip
        }

        if (newTxs > 0) setTxCount((prev) => prev + newTxs);
        if (newEvents > 0) setEventCount((prev) => prev + newEvents);
        lastBlock.current = bn;
      } finally {
        fetchingRef.current = false;
        setHasInitialized(true);
        setIsSyncing(false);
        setSyncRange(null);
      }
    }

    void fetchCounts();
  }, [blockNumber, request, fromBlock]);

  return {
    txCount,
    eventCount,
    isInitializing: !hasInitialized,
    isSyncing,
    loading: !hasInitialized || isSyncing,
    syncRange,
  };
}
